import { Text, Field, LinkField, Item, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';

import Link from 'components/helpers/Link/Link';
import { RecordOfFields } from 'models/fields';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import styles from './LinkListing.module.scss';

const cx = classNames.bind(styles);

type LinkItemFields = {
  linkDestination: LinkField;
  linkText: Field<string>;
};

type LinkItem = {
  fields: LinkItemFields;
};

type LinkListingFields = {
  linkItems?: LinkItem[];
  linkTitle: Field<string>;
  linkTitleHeadingLevel: Item;
  linkTitleHeadingStyle: Item;
  viewMoreLinkDestination: LinkField;
  viewMoreLinkText: Field<string>;
};

type LinkListingProps = ComponentProps & {
  fields: LinkListingFields;
};

const LinkListing = (props: LinkListingProps): React.JSX.Element => {
  const { className, fields } = props;
  const { linkItems, linkTitle, viewMoreLinkDestination, viewMoreLinkText } = fields;

  /** Determines if the link component is being viewed in Experience Editor. */
  const { sitecoreContext } = useSitecoreContext();
  const containerClass = sitecoreContext.pageEditing
    ? styles['link-listing-links-editing']
    : styles['link-listing-links'];

  const titleLevelItemField = fields.linkTitleHeadingLevel?.fields as RecordOfFields;
  const titleLevelValue = getFieldValue<string>(titleLevelItemField, 'Value') || 'h2';
  const titleStyleItemField = fields.linkTitleHeadingStyle?.fields as RecordOfFields;
  const titleStyleValue = getFieldValue<string>(titleStyleItemField, 'Value') || 'font-style-xl';

  return (
    <div className={cx('link-listing', className)}>
      <Text
        field={linkTitle}
        tag={titleLevelValue}
        encode={sitecoreContext.pageEditing}
        className={cx('link-listing-title', { [titleStyleValue]: true })}
      />
      {viewMoreLinkText?.value !== '' && viewMoreLinkDestination?.value?.href !== '' && (
        <Link
          className={cx('view-more-link')}
          field={viewMoreLinkDestination}
          icon="carat"
          showLinkTextWithChildrenPresent={false}
        >
          <Text field={viewMoreLinkText} encode={sitecoreContext.pageEditing} />
        </Link>
      )}
      {linkItems && linkItems?.length > 0 && (
        <div className={containerClass}>
          {linkItems?.map((item: LinkItem, index: number) => {
            return item.fields?.linkDestination ? (
              <Link
                key={index}
                className={cx('link')}
                field={item.fields?.linkDestination}
                showLinkTextWithChildrenPresent={false}
                icon="carat"
              >
                <Text field={item.fields?.linkText} encode={sitecoreContext.pageEditing} />
              </Link>
            ) : (
              <></>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default compose<LinkListingProps>(withValidDatasourceCheck(), withStyles())(LinkListing);
